'use strict';

import axios from 'axios';
import numeral from 'numeral';
import {
  getStaCorrectionFactor,
  usdPerTonneToPpl,
  calculateStaOnDuty,
  calculateStaOnProduct,
  staPpl,
  getIceSymbol,
  calculateUsdDifferential,
  bioRuleUsdPerTonneToPpl,
  bioRulePpl,
} from "./calculations";
import {
  getProductRangeIds,
  getBaseProductId,
  getStraightProductId,
  getBaseSettlementId,
  getBioSettlementId,
  getBioProductId,
  getProductDensityReferenceId,
} from "./products";
import { rulesEnum } from "@/util/enums/rules";
import store from "../../store/store";

function getMappedRuleIdForExclusion(costAdditionScope, ruleIndex){
  return `${costAdditionScope}-${ruleIndex}`;
}

function isRuleInExclusionList(costAdditionScope, ruleIndex, exclusionList, exclusionType){
  const mappedRuleId = getMappedRuleIdForExclusion(costAdditionScope, ruleIndex);

  if (exclusionType){ // Check if this rule is excluded for a specific reason (e.g. removal or overriding)
    return exclusionList.some(e => e.mappedRuleId === mappedRuleId && e.type === exclusionType);
  } else { // Check if this rule is excluded for any reason
    const mappedRuleIdsToExclude = exclusionList.map(e => e.mappedRuleId);
    return mappedRuleIdsToExclude.includes(mappedRuleId);
  }
}

function getOfferWithCostAdditions(offer, costAdditionRules, overridingCostAdditionRules, metadata, buyerAccountId) {
  const isSellerImpersonatingBuyer = buyerAccountId != null;
  const { product, fxSettle, iceLive, productIds, staMatrix, eOfferExclusion } = metadata;
  let offerToReturn = offer;
  if (!isSellerImpersonatingBuyer) offerToReturn.price -= offer.premium;
  offerToReturn.adjustedPremium = 0;

  const blendedProductIds = [productIds.BlendedDieselBioId, productIds.BlendedGasoilId];
  const isBlendedProduct = blendedProductIds.includes(offer.fuel_id);
  const productRangeIds = getProductRangeIds(productIds, offer.fuel_id); // Create a range of product IDs to match during filtering

  if (isSellerImpersonatingBuyer) {
    let customerMarginCostAdditionPpl = 0;
    // Calculate the total cost addition for customer margin rules in ppl
    for (const rule of costAdditionRules.filter(
      (rule) => rule.targets.findIndex((target) => target.type === "ACCOUNT") > -1
    )) {
      customerMarginCostAdditionPpl += calculateCostAdditionPpl(
        rule,
        product,
        isBlendedProduct,
        fxSettle,
        iceLive,
        productIds,
        staMatrix
      );
    }
    const offerPriceWithoutCustomerMargin = offerToReturn.price - customerMarginCostAdditionPpl;
    offerToReturn.basePrice = offer.price = offerPriceWithoutCustomerMargin; // Assign the price without customer margin as the offer base price
  }

  // Replace existing rules with the overriding rules from the component
  for (const overridingRule of overridingCostAdditionRules) {
    const ruleToOverrideIndex = costAdditionRules.findIndex((rule) => rule._id && rule._id === overridingRule._id);
    if (ruleToOverrideIndex > -1) {
      // Replace an existing rule with the overriding rule
      costAdditionRules[ruleToOverrideIndex] = overridingRule;
    } else {
      // This is a new rule, so let's add it to the list
      costAdditionRules.push(overridingRule);
    }
  }

  // Filter cumulative rules in case we need to apply/remove rules to/from an offer
  const { applicableCumulativeRules, excludedRules, overridingOfferRulesToDelete } = filterCumulativeRules(
    costAdditionRules,
    offer,
    productRangeIds,
    eOfferExclusion,
    true,
    buyerAccountId
  );
  offerToReturn.pricingAuditTrail = applicableCumulativeRules; // Assign updated pricing audit trail
  offerToReturn.excludedRuleIds = excludedRules.map((rule) => rule._id); // Assign the list of IDs for excluded global cost additions
  offerToReturn.overridingOfferRulesToDelete = overridingOfferRulesToDelete; // Assign the list of offer-specific overriding rules that can be deleted when rule changes are saved

  if (!product) {
    // If product is empty, this means that we cannot do any further calculation
    offerToReturn.adjustedPremium = offerToReturn.premium;
    return offerToReturn;
  }

  for (const rule of applicableCumulativeRules) {
    const isCustomerMarginRule = rule.targets.findIndex((target) => target.type === "ACCOUNT") > -1; // Indicate whether this is a customer margin rule or not

    // Add cost addition to the adjusted premium and final price, either IF seller is not impersonating a buyer or IF seller is impersonating buyer AND the rule is a customer margin rule
    if (!isSellerImpersonatingBuyer || isCustomerMarginRule) {
      const costAdditionPpl = calculateCostAdditionPpl(
        rule,
        product,
        isBlendedProduct,
        fxSettle,
        iceLive,
        productIds,
        staMatrix
      );
      offerToReturn.price += costAdditionPpl;
      offerToReturn.adjustedPremium += costAdditionPpl;
    }
  }

  return offerToReturn;
}

function simpleCalculateCostAdditionPpl(rule, productId, orderMarketWindow, pricingAuditTrail) {
  if (!orderMarketWindow) {
    return 0;
  }
  const blendedProductConfig = store.getters["metadata/blendedProductConfig"];
  const isBlendedProduct = blendedProductConfig.includes(productId);

  const productConfig = store.getters["metadata/productConfig"];
  const staMatrix = store.getters["metadata/staMatrix"];
  const metadata = { staMatrix, productIds: productConfig };

  const straightProductId = getStraightProductId(productConfig, productId);
  const bioProductId = getBioProductId(productConfig, productId);
  const bioProductDensityId = getProductDensityReferenceId(productConfig, bioProductId);

  const iceLiveRule = getIceLiveRule(pricingAuditTrail);
  const iceLive = orderMarketWindow.prices.find((p) => p.symbol === getIceSymbol(iceLiveRule.action.value)).live;

  const fxSettlementRule = getFxSettlementRule(pricingAuditTrail);

  const dutyRule = getDutyRule(productId, pricingAuditTrail);
  const densityRule = getDensityRule(straightProductId, pricingAuditTrail);
  const bioDensityRule = getDensityRule(bioProductDensityId, pricingAuditTrail);
  const productSettlementRule = getProductSettlementRule(productId, pricingAuditTrail);
  const iceSettlement = getIceSettlementRule(pricingAuditTrail);
  const bioBlendRule = getBioBlendRule(pricingAuditTrail);

  const product = {
    fuel_id: productId,
    duty: dutyRule.action.value,
    density: densityRule.action.value,
    bioDensity: bioDensityRule && bioDensityRule.action.value,
    bioBlend: bioBlendRule && bioBlendRule.action.value,
    usdDiff: calculateUsdDifferential(productSettlementRule.action.value, iceSettlement),
  };

  const { action } = rule;
  const { unitOfMeasurement, value, targetAttribute } = action;

  return convertValueToPpl(
    unitOfMeasurement,
    value,
    product,
    fxSettlementRule.action.value,
    iceLive,
    isBlendedProduct,
    metadata,
    targetAttribute === rulesEnum.eTargetAttributeType.BioBlend
  );
}

function calculateCostAdditionPpl(rule, product, isBlendedProduct, fxSettle, iceLive, productIds, staMatrix) {
  const { unitOfMeasurement, value, targetAttribute } = rule.action;
  switch (unitOfMeasurement) {
    case "PPL":
      if (targetAttribute === rulesEnum.eTargetAttributeType.BioBlend) {
        return Number(bioRulePpl(value, product.bioBlend));
      }
      return Number(value);
    case "USD/METRIC_TONNE":
    case "CELSIUS": {
      return convertValueToPpl(
        unitOfMeasurement,
        Number(value),
        product,
        fxSettle,
        iceLive,
        isBlendedProduct,
        {
          productIds,
          staMatrix,
        },
        targetAttribute === rulesEnum.eTargetAttributeType.BioBlend
      );
    }
    default:
      return 0;
  }
}

function convertValueToPpl(
  unitOfMeasurement,
  value,
  product,
  fxSettle,
  iceLive,
  blended = false,
  metadata,
  bioBlendRule = false
) {
  switch (unitOfMeasurement) {
    case "USD/METRIC_TONNE": {
      let density = product.density;
      if (blended) {
        density = product.bioDensity;
      }
      if (!bioBlendRule) {
        return usdPerTonneToPpl(value, fxSettle, density);
      }

      const { bioBlend } = product;
      return bioRuleUsdPerTonneToPpl(value, fxSettle, density, bioBlend);
    }
    case "CELSIUS": {
      const { fuel_id, duty, density, usdDiff } = product;
      const { productIds, staMatrix } = metadata;
      const baseProductId = getBaseProductId(productIds, fuel_id);

      const staCorrectionFactor = getStaCorrectionFactor(Number(value), baseProductId, staMatrix);
      const staOnDuty = calculateStaOnDuty(staCorrectionFactor, duty, density, fxSettle);
      const staOnProduct = calculateStaOnProduct(iceLive, usdDiff, staCorrectionFactor);

      return staPpl(staOnDuty, staOnProduct, fxSettle, density);
    }
    case "PPL": {
      if (bioBlendRule) {
        return bioRulePpl(value, product.bioBlend);
      }
      return value;
    }
    default:
      return 0;
  }
}

function convertCelsiusRuleToPpl(productId, pricingAuditTrail, ruleValue, orderMarketWindow) {
  const productConfig = store.getters["metadata/productConfig"];
  const blendedProductConfig = store.getters["metadata/blendedProductConfig"];
  const staMatrix = store.getters["metadata/staMatrix"];
  
  const straightProductId = getStraightProductId(productConfig, productId);
  
  // Find the relevant rules to be used in conversion later
  const dutyRule = getDutyRule(productId, pricingAuditTrail);
  const densityRule = getDensityRule(straightProductId, pricingAuditTrail);
  const productSettlementRule = getProductSettlementRule(productId, pricingAuditTrail);

  const iceSettlementRule = getIceSettlementRule(pricingAuditTrail);
  const iceLiveRule = getIceLiveRule(pricingAuditTrail);
  const fxSettlementRule = getFxSettlementRule(pricingAuditTrail);

  if (
    !orderMarketWindow ||
    !dutyRule ||
    !densityRule ||
    !productSettlementRule ||
    !fxSettlementRule ||
    !iceLiveRule ||
    !iceSettlementRule
  ) {
    return "N/A";
  } else {
    const iceLive = orderMarketWindow.prices.find((p) => p.symbol === getIceSymbol(iceLiveRule.action.value)).live;
    const iceSettlement = orderMarketWindow.prices.find(
      (p) => p.symbol === getIceSymbol(iceSettlementRule.action.value)
    ).settle;
    const product = {
      fuel_id: productId,
      duty: dutyRule.action.value,
      density: densityRule.action.value,
      usdDiff: calculateUsdDifferential(productSettlementRule.action.value, iceSettlement),
    };
    const isBlendedProduct = blendedProductConfig.includes(productId);
    const metadata = {
      productIds: productConfig,
      staMatrix,
    };

    return convertValueToPpl(
      rulesEnum.eUnitType.Celsius.id,
      ruleValue,
      product,
      fxSettlementRule.action.value,
      iceLive,
      isBlendedProduct,
      metadata
    );
  }
}

function getIceSettlementRule(pricingAuditTrail) {
  const { eTargetAttributeType } = rulesEnum;
  return pricingAuditTrail.find(({ action }) => action.targetAttribute === eTargetAttributeType.IceSettle);
}
function getIceLiveRule(pricingAuditTrail) {
  const { eTargetAttributeType } = rulesEnum;
  return pricingAuditTrail.find(({ action }) => action.targetAttribute === eTargetAttributeType.IceLive);
}

function getFxSettlementRule(pricingAuditTrail) {
  const { eTargetAttributeType } = rulesEnum;
  return pricingAuditTrail.find((rule) => rule.action.targetAttribute === eTargetAttributeType.Fx);
}

function getProductSettlementRule(productId, pricingAuditTrail) {
  const { eTargetAttributeType, eTargetType } = rulesEnum;
  const productConfig = store.getters["metadata/productConfig"];
  const productIdForSettlement = getBaseSettlementId(productConfig, productId);

  return pricingAuditTrail.find(
    ({ targets, action }) =>
      action.targetAttribute === eTargetAttributeType.ProductSettle &&
      targets.find(({ type, ids }) => type === eTargetType.Product && ids.includes(productIdForSettlement))
  );
}

function getDutyRule(productId, pricingAuditTrail) {
  const { eTargetAttributeType, eTargetType } = rulesEnum;
  const productConfig = store.getters["metadata/productConfig"];
  const straightProductId = getStraightProductId(productConfig, productId);

  // Find the relevant rules to be used in conversion later
  return pricingAuditTrail.find(
    ({ targets, action }) =>
      action.targetAttribute === eTargetAttributeType.Duty &&
      targets.find(({ type, ids }) => type === eTargetType.Product && ids.includes(straightProductId))
  );
}
function getDensityRule(productId, pricingAuditTrail) {
  const { eTargetAttributeType, eTargetType } = rulesEnum;

  return pricingAuditTrail.find(
    ({ targets, action }) =>
      action.targetAttribute === eTargetAttributeType.Density &&
      targets.find(({ type, ids }) => type === eTargetType.Product && ids.includes(productId))
  );
}
function getBioBlendRule(pricingAuditTrail) {
  const { eTargetAttributeType, eType } = rulesEnum;
  return pricingAuditTrail.find(
    ({ type, action }) => type === eType.Precedence && action.targetAttribute === eTargetAttributeType.BioBlend
  );
}

function groupOverridingRulesWithGlobalRules(cumulativeRules) {
  
  const globalRules = cumulativeRules.filter(rule => rule.priority === rulesEnum.ePriority.Global);
  const offerRules = cumulativeRules.filter(rule => rule.priority === rulesEnum.ePriority.Offer);
  let rulesMap = {};
  let nonOverridingRules = [];
  
  // Create an object with the rule id and the rule object as key-value pairs
  for (const rule of globalRules) {
    rulesMap[rule._id] = [rule];
  }
  
  for (const rule of offerRules) {
    const { ruleToOverride } = rule;
    if (ruleToOverride in rulesMap) {
      rulesMap[ruleToOverride].push(rule);
    } else {
      nonOverridingRules.push(rule);
    }
  }
  return [...Object.values(rulesMap).flatMap(rules => rules), ...nonOverridingRules];
}

function filterCumulativeRulesByTarget(
  rules,
  offer,
  productRangeIds,
  eOfferExclusion,
  includeOnlyContractSpecificRules = true,
  buyerAccountId
) {
  const { terminal_id, networkId, isContract, fuel_measurement_id, price_input_type_id, type_id } = offer;

  // Filter the rules by target type and IDs
  return rules.filter((rule) => {
    const { targets, offerExclusions } = rule;
    const targetOffer = targets.find(({ type }) => type === "OFFER");
    const targetProduct = targets.find(({ type }) => type === "PRODUCT");
    const targetTerminal = targets.find(({ type }) => type === "TERMINAL");
    const targetNetwork = targets.find(({ type }) => type === "NETWORK");
    const targetAccount = targets.find(({ type }) => type === "ACCOUNT");

    if (targetAccount) {
      // Check whether rule is targetting the selected buyer or not
      const ids = targetAccount.ids.map((account) => account.id || account);
      if (!ids.includes(buyerAccountId)) return false;
    }

    // Ensure this offer is not being excluded via rule's offerExclusions list
    if (offerExclusions && offerExclusions.length > 0) {
      if (isContract && includeOnlyContractSpecificRules) {
        if (type_id === "FIXED" && offerExclusions.includes(eOfferExclusion.FixedContract)) return false;
        else if (type_id === "TRACKING" && offerExclusions.includes(eOfferExclusion.LiveContract)) return false;
        else if (type_id === "D1" && offerExclusions.includes(eOfferExclusion.D1Contract)) return false;
        else if (type_id === "D2" && offerExclusions.includes(eOfferExclusion.D2Contract)) return false;
      } else {
        if (type_id === "FIXED" && offerExclusions.includes(eOfferExclusion.FixedSpot)) return false;
        else if (type_id === "TRACKING" && offerExclusions.includes(eOfferExclusion.LiveSpot)) return false;
        else if (type_id === "D1" && offerExclusions.includes(eOfferExclusion.D1Spot)) return false;
        else if (type_id === "D2" && offerExclusions.includes(eOfferExclusion.D2Spot)) return false;
      }
      if (price_input_type_id === "TOTAL" && offerExclusions.includes(eOfferExclusion.PriceInputTypeTotal))
        return false;
      // temperature targeting rules are for SPOT AND AMBIENT offers only
      if (
        (isContract || fuel_measurement_id === "STANDARD") &&
        offerExclusions.includes(eOfferExclusion.StandardMeasurement)
      )
        return false;
    }

    if (targetOffer) {
      const ids = targetOffer.ids.map((id) => id.toString());
      if (!ids.includes(offer._id)) return false;
    } else if (targetProduct && targetTerminal && targetNetwork) {
      const targetProductIds = targetProduct.ids.map((product) => product._id || product);
      const targetTerminalIds = targetTerminal.ids.map((terminal) => terminal._id || terminal);
      const targetNetworkIds = targetNetwork.ids.map((network) => network._id || network);
      // Cumulative rule needs to target both the offer's terminal/network & product in this scenario, otherwise remove add to rule blacklist
      if (
        !targetProductIds.some((id) => productRangeIds.includes(id)) ||
        (terminal_id && !targetTerminalIds.includes(terminal_id))
      ) {
        return false;
      } else if (
        !targetProductIds.some((id) => productRangeIds.includes(id)) ||
        (networkId && !targetNetworkIds.includes(networkId))
      ) {
        return false;
      }
    } else if (targetProduct && targetTerminal) {
      const targetProductIds = targetProduct.ids.map((product) => product._id || product);
      const targetTerminalIds = targetTerminal.ids.map((terminal) => terminal._id || terminal);
      // Cumulative rule needs to target both the offer's terminal & product in this scenario, otherwise remove add to rule blacklist
      if (!targetProductIds.some((id) => productRangeIds.includes(id)) || !targetTerminalIds.includes(terminal_id))
        return false;
    } else if (targetProduct && targetNetwork) {
      const targetProductIds = targetProduct.ids.map((product) => product._id || product);
      const targetNetworkIds = targetNetwork.ids.map((network) => network._id || network);
      // Cumulative rule needs to target both the offer's network & product in this scenario, otherwise remove add to rule blacklist
      if (!targetProductIds.some((id) => productRangeIds.includes(id)) || !targetNetworkIds.includes(networkId))
        return false;
    } else if (targetProduct) {
      const ids = targetProduct.ids.map((id) => id.toString());
      if (!ids.some((id) => productRangeIds.includes(id))) return false;
    } else if (targetTerminal) {
      const ids = targetTerminal.ids.map((id) => id.toString());
      if (!ids.includes(terminal_id)) return false;
    } else if (targetNetwork) {
      const ids = targetNetwork.ids.map((id) => id.toString());
      if (!ids.includes(networkId)) return false;
    }
    // Passed validation, return true
    return true;
  });
}

function filterCumulativeRules(
  rules,
  offer,
  productRangeIds,
  eOfferExclusion,
  includeOnlyContractSpecificRules = true,
  buyerAccountId
) {
  let applicableCumulativeRules = [];
  let excludedRules = [];
  let overridingOfferRulesToDelete = [];

  // Filter the rules by target type and IDs
  const filteredRulesByTarget = filterCumulativeRulesByTarget(
    rules,
    offer,
    productRangeIds,
    eOfferExclusion,
    includeOnlyContractSpecificRules,
    buyerAccountId
  );

  // Work out applicable and excluded rules to be returned
  const excludedRuleIds = filteredRulesByTarget
    .map((rule) => (rule.ruleToOverride ? rule.ruleToOverride.toString() : null))
    .filter((id) => id && filteredRulesByTarget.map((rule) => rule._id).includes(id));
  excludedRules = filteredRulesByTarget.filter((rule) => excludedRuleIds.includes(rule._id));
  applicableCumulativeRules = filteredRulesByTarget.filter((rule) => !excludedRuleIds.includes(rule._id));

  // Work out overriding rules that will be invalid once rule changes are saved, so they can be deleted
  const overridingOfferRules = rules.filter(
    (rule) =>
      rule.ruleToOverride && rule.targets.find((target) => target.type === "OFFER" && target.ids.includes(offer._id))
  ); // Let's get a list of overriding rules that are specific for this offer
  const filteredRuleIdsByTarget = filteredRulesByTarget.filter((rule) => rule._id).map((rule) => rule._id); // Get a list of relevant rule IDs for this offer (i.e. either applicable or excluded)

  // Now, go through overriding rules for this offer and add the rule to a list of rules to be deleted, if it is not in the list of filtered rule IDs by target
  for (const rule of overridingOfferRules) {
    if (!filteredRuleIdsByTarget.includes(rule.ruleToOverride)) {
      const { _id, ruleToOverride } = rule;
      overridingOfferRulesToDelete.push({ _id, ruleToOverride });
    }
  }

  return { applicableCumulativeRules, excludedRules, overridingOfferRulesToDelete };
}

function removeOverriddenRules(rules = []){
  const overriddenRuleIds = rules.filter(rule => rule.ruleToOverride).map(rule => rule.ruleToOverride);
  return rules.filter(rule => !overriddenRuleIds.includes(rule._id));
}

function filterRulesByPriority(rules = [], priority = "GLOBAL"){
  return rules.filter(rule => rule.priority === priority);
}

function filterPricingRules(rules = [], priority = null, ruleType = null, offerExclusions = null){
  if (priority)
    rules = rules.filter(rule => rule.priority === priority);
  if (ruleType)
    rules = rules.filter(rule => rule.type === ruleType);
  if (offerExclusions)
    rules = rules.filter(rule => rule.offerExclusions && rule.offerExclusions.every(exclusion => offerExclusions.includes(exclusion)));

  return rules;
}

async function saveRules(rules = [], mapTargetIds = false, ruleType, shouldCheckUpdateRecency = false) {
  const rulesList = [];

  for (let rule of rules) {
    let ruleProjection = getFormattedRule(rule, mapTargetIds);
    rulesList.push(ruleProjection);
  }

  const newRules = rulesList.filter((r) => !r._id);
  const updatedRules = rulesList
    .filter((r) => r._id)
    .map((r) => {
      return { ...r, ruleId: r._id };
    });

  // Redirect the post/put paths based on the rule type I.E daily settlement or cost addition rules
  let putPath = "";
  let postPath = "";
  switch (ruleType) {
    case "costAdditions": {
      postPath = "/api/pricing/rules/cost-additions";
      putPath = shouldCheckUpdateRecency ? "/api/pricing/rules/cost-additions-unchecked" :  "/api/pricing/rules/cost-additions";
      break;
    }
    default: {
      postPath = "/api/pricing/rules/cost-additions";
      putPath = "/api/pricing/rules";
      break;
    }
  }
  let res;
  if(newRules.length > 0) {
    const {data} =  await axios.post(postPath, newRules);
    res = data;
  };
  if (updatedRules.length > 0) {
    const {data} =  await axios.put(putPath, updatedRules);
    res = data;
  }
  return res;
}

async function deleteRules(rules = []) {
  const ruleIdsToDelete = rules.map((rule) => rule._id).filter((id) => id); // Only retain rules with an ID
  if (ruleIdsToDelete.length > 0) await axios.delete("/api/pricing/rules", { data: { ruleIdsToDelete } });
}

function getFormattedRule(rule = {}, mapTargetIds = false) {
  let ruleProjection = JSON.parse(JSON.stringify(rule)); // Deep copy the object to avoid data binding after mapping
  // Map target IDs if needed
  if (mapTargetIds) {
    for (let i = 0; i < ruleProjection.targets.length; i++) {
      const targetIdsProjection = ruleProjection.targets[i].ids.map((t) => t._id || t.id || t);
      ruleProjection.targets[i].ids = targetIdsProjection;
    }
  }

  ruleProjection.action.value = Number(rule.action.value); // Ensure that we are saving value of type Number

  return ruleProjection;
}

function getRuleScopeOfferExclusions(ruleScope, eRuleScope, eOfferExclusion){
  switch (ruleScope){
    case eRuleScope.Global:
      return [
        eOfferExclusion.FixedSpot,
        eOfferExclusion.FixedContract,
        eOfferExclusion.LiveContract,
        eOfferExclusion.D1Contract,
        eOfferExclusion.D2Contract,
        eOfferExclusion.PriceInputTypeTotal
      ];
    case eRuleScope.Bio:
      return [
        eOfferExclusion.FixedSpot,
        eOfferExclusion.FixedContract,
        eOfferExclusion.LiveContract,
        eOfferExclusion.D1Contract,
        eOfferExclusion.D2Contract,
        eOfferExclusion.PriceInputTypeTotal
      ];
    case eRuleScope.Customer:
      return [
        eOfferExclusion.FixedContract,
        eOfferExclusion.LiveContract,
        eOfferExclusion.D1Contract,
        eOfferExclusion.D2Contract
      ];
    case eRuleScope.Temperature:
      return [
        eOfferExclusion.FixedSpot,
        eOfferExclusion.FixedContract,
        eOfferExclusion.LiveContract,
        eOfferExclusion.D1Contract,
        eOfferExclusion.D2Contract,
        eOfferExclusion.PriceInputTypeTotal,
        eOfferExclusion.StandardMeasurement
      ];
  }
}

function applyTargetToRules(targetType, targetIds = [], rules, resetTargetIds = false){
  for (let rule of rules){
    // Check whether a matching target type already exists in this rule
    const matchingTarget = rule.targets.find(target => target.type === targetType);

    if (matchingTarget){
      if (resetTargetIds){ // Let's reset the list of target IDs
        matchingTarget.ids = targetIds;
      } else {
        // Loop through list of target IDs sent and add them to the list if it's not there already
        for (const targetId of targetIds){
          if (!matchingTarget.ids.includes(targetId)) matchingTarget.ids.push(targetId);
        }
      }
    } else {
      // Add a new target object with type and IDs sent
      let targetProjection = { 
        type: targetType, 
        ids: [] 
      };
      if (targetIds.length > 0) targetProjection.ids = targetIds;

      rule.targets.push(targetProjection);
    }
  }
  
  return rules;
}

function filterOfferSpecificRulesByOfferId(offerId, rules = []){
  let rulesToReturn = [];

  for (const rule of JSON.parse(JSON.stringify(rules))){ // Use deep copy object to prevent binding
    const { targets } = rule;
    
    if (targets.find(target => target.type === 'OFFER' && target.ids.includes(offerId)))
      rulesToReturn.push(rule);
  }

  return rulesToReturn;
}

function getPricingRuleName(rule, bioProductIds){
  const { name, action, targets } = rule;
  const { eTargetAttributeType, eTargetType, eUnitType } = rulesEnum;
  let isBioProductRule = false;
  if (targets){
    isBioProductRule = targets.find(target => 
      target.type === eTargetType.Product // Rule is targeting product IDs
      && target.ids.length == 1 // There is only one product ID being targeted to ensure this is a single product-specific rule
      && target.ids.find(id => bioProductIds.includes(id)) // Product ID targeted is a bio product ID
    );
  }

  switch (action.targetAttribute){
    case eTargetAttributeType.ProductSettle:
      return isBioProductRule ? "Bio-element Settlement" : "Product Settlement";
    case eTargetAttributeType.IceLivePrice:
      return `LS Gasoil Future`;
    case eTargetAttributeType.Fx:
      return `${eUnitType.GbpUsd.display} Settlement`;
    case eTargetAttributeType.FxLive:
      return `${eUnitType.GbpUsd.display} Live Exchange Basis`;
    case eTargetAttributeType.Density:
      return isBioProductRule ? "Bio Density" : "Density";
    case eTargetAttributeType.Duty:
      return isBioProductRule ? "Bio Duty" : "Duty";
    default:
      return name;
  }
}

function getPricingRuleValueWithUnit(ruleAction){
  const { targetAttribute, value, unitOfMeasurement } = ruleAction;
  const unitSelected = Object.values(rulesEnum.eUnitType).find((unit) => unit.id === unitOfMeasurement);
  //Need to maybe do something here
  switch (targetAttribute) {
    case rulesEnum.eTargetAttributeType.Fx:
    case rulesEnum.eTargetAttributeType.FxLive:
      return `${numeral(value).format("0,0.0000")}`;
    default:
      return `${numeral(value).format("0,0.00")} ${unitSelected.display || unitOfMeasurement}`;
  }
}

function getMarketDataOrderRules(orderItem, marketWindow, productIds){
  const { pricingAuditTrail, pricingMethod, product_id, bioPrice } = orderItem;

  const baseProductId = getBaseSettlementId(productIds, product_id);
  const bioElementSettlementId = getBioSettlementId(productIds, product_id);
  const productSettlementRule = pricingAuditTrail.find(rule => 
    rule.action.targetAttribute === rulesEnum.eTargetAttributeType.ProductSettle
    && rule.targets.find(target => target.type === rulesEnum.eTargetType.Product && target.ids.includes(baseProductId))
  );
  const bioSettlementRule = pricingAuditTrail.find(rule => 
    rule.action.targetAttribute === rulesEnum.eTargetAttributeType.ProductSettle
    && rule.targets.find(target => target.type === rulesEnum.eTargetType.Product && target.ids.includes(bioElementSettlementId))
  );
  const fxSettlementRule = pricingAuditTrail.find(rule => rule.action.targetAttribute === rulesEnum.eTargetAttributeType.Fx);

  const { fx, ice, prices } = marketWindow;

  // Mock a live FX rule with the order's live FX price
  const liveFxRule = {
    action: {
      targetAttribute: rulesEnum.eTargetAttributeType.FxLive,
      value: fx.live,
      unitOfMeasurement: rulesEnum.eUnitType.GbpUsd.id
    }
  }

  // Mock a live ICE rule with the order's live ICE price
  const iceLiveRule = pricingAuditTrail.find(rule => rule.action.targetAttribute === rulesEnum.eTargetAttributeType.IceLive);
  const iceSelected = iceLiveRule
    ? prices.find((priceSymbol) => priceSymbol.symbol === getIceSymbol(iceLiveRule.action.value))
    : ice;
  const iceLivePremiumRule = pricingAuditTrail.find(rule => rule.action.targetAttribute === rulesEnum.eTargetAttributeType.IceLivePremium);
  const iceLivePremium = iceLivePremiumRule ? iceLivePremiumRule.action.value : 0;
  const liveIceRule = {
    action: {
      targetAttribute: rulesEnum.eTargetAttributeType.IceLivePrice,
      value: iceSelected.live + iceLivePremium,
      unitOfMeasurement: rulesEnum.eUnitType.UsdPerMetricTonne.id
    }
  }

  if (pricingMethod === 'Live'){
    if (bioPrice === 'Split'){
      return [
        productSettlementRule,
        bioSettlementRule,
        liveIceRule,
        fxSettlementRule,
        liveFxRule
      ];
    } else {
      return [
        productSettlementRule,
        liveIceRule,
        fxSettlementRule,
        liveFxRule
      ];
    }
  } else {
    if (bioPrice === 'Split'){
      return [
        productSettlementRule,
        bioSettlementRule,
        fxSettlementRule
      ];
    } else {
      return [
        productSettlementRule,
        fxSettlementRule
      ];
    }
  }
}

function getProductSpecificRules(orderItem, productIds){
  const { pricingAuditTrail, product_id } = orderItem;
  const productRangeIds = getProductRangeIds(productIds, product_id);

  // Add a list of cost additions applied
  const filteredPricingRules = pricingAuditTrail.filter(rule => {
    const { type, action, targets } = rule;
    if (type === rulesEnum.eType.Cumulative){ // Include cumulative rules
      return true;
    } else if (action.targetAttribute === rulesEnum.eTargetAttributeType.Density){ // Include density rules
      // Only include the base and bio products' density
      const targetProduct = targets.find(target => target.type === rulesEnum.eTargetType.Product && target.ids.some(productId => productRangeIds.includes(productId)));
      if (targetProduct) return true;
    } else if (action.targetAttribute === rulesEnum.eTargetAttributeType.Duty){ // Include duty rules
      // Only include the base and bio products' duty
      const targetProduct = targets.find(target => target.type === rulesEnum.eTargetType.Product && target.ids.some(productId => productRangeIds.includes(productId)));
      if (targetProduct) return true;
    }
  });

  return filteredPricingRules;
}

function isCumulativeRuleGroupRelevantForOffer(costAdditionScope, ruleGroupKey, offer, blendedProductConfig){
  const { fuel, fuel_measurement_id, isContract } = offer;
  switch (ruleGroupKey){
    case costAdditionScope.Global:
      // Global cost addition is always relevant
      return true;
    case costAdditionScope.Bio:
      // Bio rule is only relevant to blended/split quote offers
      return blendedProductConfig.includes(fuel && fuel._id);
    case costAdditionScope.Temperature:
      // Temperature rule is only relevant to spot (non-contract) AND ambient offers
      return !isContract && fuel_measurement_id === 'AMBIENT';
  }
}

export {
  getMappedRuleIdForExclusion,
  isRuleInExclusionList,
  getOfferWithCostAdditions,
  convertCelsiusRuleToPpl,
  convertValueToPpl,
  calculateCostAdditionPpl,
  groupOverridingRulesWithGlobalRules,
  filterCumulativeRulesByTarget,
  filterCumulativeRules,
  removeOverriddenRules,
  filterRulesByPriority,
  filterPricingRules,
  saveRules,
  deleteRules,
  getRuleScopeOfferExclusions,
  applyTargetToRules,
  filterOfferSpecificRulesByOfferId,
  getPricingRuleName,
  getPricingRuleValueWithUnit,
  getMarketDataOrderRules,
  getProductSpecificRules,
  isCumulativeRuleGroupRelevantForOffer,
  simpleCalculateCostAdditionPpl,
};